import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from '../hooks/use-toast';
import { translations } from '../i18n/translations';
import {
  fetchCart, apiAddToCart, apiUpdateCart, apiRemoveCart,
  fetchWishlist, apiToggleWishlist,
  apiPlaceOrder,
  authMe, authLogin, authRegister, authLogout
} from '../api/client';

const StoreContext = createContext(null);
const LS_LANG = 'feron_lang';

// --- helpers ------------------------------------------------------------------
const getLang = () => { try { return localStorage.getItem(LS_LANG) || 'en'; } catch { return 'en'; } };
const tr = (key) => {
  const lang = getLang();
  return (translations[lang] && translations[lang][key]) || translations.en[key] || key;
};

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], count: 0, total: 0, mrpTotal: 0 });
  const [wishlist, setWishlist] = useState([]);
  const [user, setUser] = useState(null);   // null while checking, false = anon, object = authed
  const [authChecked, setAuthChecked] = useState(false);
  const [loading, setLoading] = useState(true);

  // Refresh from backend
  const refreshCart = useCallback(async () => {
    try {
      const data = await fetchCart();
      setCart(data);
    } catch (err) { console.warn('[Store] cart fetch failed:', err.message); }
  }, []);

  const refreshWishlist = useCallback(async () => {
    try {
      const data = await fetchWishlist();
      setWishlist(data.items || []);
    } catch (err) { console.warn('[Store] wishlist fetch failed:', err.message); }
  }, []);

  // Check existing session cookie once at mount
  useEffect(() => {
    // Skip auth check when returning from Google OAuth (AuthCallback handles it)
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setAuthChecked(true);
      return;
    }
    (async () => {
      try {
        const me = await authMe();
        setUser(me);
      } catch {
        setUser(false);
      } finally {
        setAuthChecked(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (!authChecked) return;
    (async () => {
      setLoading(true);
      await Promise.all([refreshCart(), refreshWishlist()]);
      setLoading(false);
    })();
  }, [authChecked, user, refreshCart, refreshWishlist]);

  // --- Mutations ---
  const addToCart = useCallback(async (product, size, qty = 1) => {
    try {
      const data = await apiAddToCart(product.id, size, qty);
      setCart(data);
      toast({ title: tr('addedToBag'), description: `${product.name} (${size})` });
    } catch (err) {
      console.error('[Store] add to cart failed:', err);
      toast({ title: 'Could not add to bag', description: err.message });
    }
  }, []);

  const updateQty = useCallback(async (itemId, qty) => {
    try {
      const data = await apiUpdateCart(itemId, Math.max(1, qty));
      setCart(data);
    } catch (err) { console.error('[Store] update qty failed:', err); }
  }, []);

  const removeFromCart = useCallback(async (itemId) => {
    try {
      const data = await apiRemoveCart(itemId);
      setCart(data);
    } catch (err) { console.error('[Store] remove failed:', err); }
  }, []);

  const toggleWishlist = useCallback(async (product) => {
    try {
      const res = await apiToggleWishlist(product.id);
      toast({
        title: res.action === 'added' ? tr('addedToWishlist') : tr('removedFromWishlist'),
        description: res.action === 'added' ? product.name : undefined
      });
      await refreshWishlist();
    } catch (err) { console.error('[Store] toggle wishlist failed:', err); }
  }, [refreshWishlist]);

  const isWishlisted = useCallback((id) => wishlist.some((i) => i.id === id), [wishlist]);

  const placeOrder = useCallback(async (payload) => {
    const order = await apiPlaceOrder(payload);
    await refreshCart();
    return order;
  }, [refreshCart]);

  // --- Real auth actions ---
  const login = useCallback(async (email, password) => {
    const u = await authLogin(email, password);
    setUser(u);
    return u;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const u = await authRegister(email, password, name);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    try { await authLogout(); } catch (e) { console.warn('[auth] logout request failed:', e?.message || e); }
    setUser(false);
    // Clear cart/wishlist locally; anon fallback will hydrate next
    setCart({ items: [], count: 0, total: 0, mrpTotal: 0 });
    setWishlist([]);
  }, []);

  const refreshUser = useCallback(async () => {
    try { setUser(await authMe()); } catch { setUser(false); }
  }, []);

  const value = useMemo(() => ({
    cart: cart.items,
    cartCount: cart.count,
    cartTotal: cart.total,
    cartMrpTotal: cart.mrpTotal,
    cartTotalEUR: cart.totalEUR,
    cartMrpTotalEUR: cart.mrpTotalEUR,
    wishlist,
    user: user && typeof user === 'object' ? user : null,
    isAuthenticated: !!(user && typeof user === 'object'),
    authChecked,
    loading,
    addToCart,
    updateQty,
    removeFromCart,
    toggleWishlist,
    isWishlisted,
    placeOrder,
    login,
    register,
    logout,
    refreshUser,
    refreshCart,
    refreshWishlist
  }), [cart, wishlist, user, authChecked, loading, addToCart, updateQty, removeFromCart, toggleWishlist, isWishlisted, placeOrder, login, register, logout, refreshUser, refreshCart, refreshWishlist]);

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

export const useStore = () => useContext(StoreContext);
